import { ApolloError, useQuery } from '@apollo/client'
import { GET_VERSION_CFD_RESULTS_WITH_FACILITY } from 'client/queries'
import { useMemo } from 'react'
import {
  CFD_FILE_PROPS_TYPE,
  CFD_STATUS,
  COMPOSITE_SIMULATION_TYPES,
  HEIGHTS,
  HEIGHT_VALUES,
  SIMULATION_TYPES,
} from 'config/cfd'
import { FacilityDefaults, getFacilityDefaultDetails } from 'hooks/useFacilityDefaults'
import { useParams } from 'react-router-dom'
import { array, number, object, optional, parse, string, union } from 'valibot'

export type HeightURLs = {
  [HEIGHTS.standing]: string[]
  [HEIGHTS.seated]: string[]
}

export type CoolingURLs = {
  velocity: HeightURLs
  temperature: HeightURLs
}

const getCoolingReducerInitialValue = (): CoolingURLs => ({
  velocity: {
    [HEIGHTS.standing]: [],
    [HEIGHTS.seated]: [],
  },
  temperature: {
    [HEIGHTS.standing]: [],
    [HEIGHTS.seated]: [],
  },
})

export type DestratURLs = {
  destrat: string
  velocity: HeightURLs
}

const getDestratReducerInitialValue = (): DestratURLs => ({
  destrat: '',
  velocity: {
    [HEIGHTS.standing]: [],
    [HEIGHTS.seated]: [],
  },
})

export type RadiantURLs = {
  intensity: string
}

const getRadiantHeatReducerInitialValue = (): RadiantURLs => ({
  intensity: '',
})

export type UnitHeatURLs = {
  temperatureBefore: HeightURLs
  temperatureAfter: HeightURLs
  velocityBefore: HeightURLs
  velocityAfter: HeightURLs
}

const getUnitHeatReducerInitialValue = (): UnitHeatURLs => ({
  temperatureBefore: {
    [HEIGHTS.standing]: [],
    [HEIGHTS.seated]: [],
  },
  temperatureAfter: {
    [HEIGHTS.standing]: [],
    [HEIGHTS.seated]: [],
  },
  velocityBefore: {
    [HEIGHTS.standing]: [],
    [HEIGHTS.seated]: [],
  },
  velocityAfter: {
    [HEIGHTS.standing]: [],
    [HEIGHTS.seated]: [],
  },
})

type URLObject = {
  id: string
  url: string
  fileExtension?: string | null
  fileProps?: { type: string; level: string | number }
}

const urlObjectSchema = object({
  id: string(),
  url: string(),
  fileExtension: optional(string()),
  fileProps: optional(
    object({
      type: string(),
      level: union([string(), number()]),
    })
  ),
})

const getHeatingResultsComposite = (vtkFileResults: VtkResult[]) => {
  const heatingResults = vtkFileResults?.filter(
    ({ type }) => type === SIMULATION_TYPES.radiantHeat || type === SIMULATION_TYPES.unitHeating
  )
  if (heatingResults?.length !== 2) return []
  const status = heatingResults.find(({ status }) => status !== CFD_STATUS.COMPLETE)?.status ?? CFD_STATUS.COMPLETE
  const vtkUrlGroups = Object.fromEntries(heatingResults.map(({ type, vtkUrlGroups }) => [type, vtkUrlGroups]))
  const heatingResultsComposite = {
    type: COMPOSITE_SIMULATION_TYPES.heatingComposite,
    status,
    vtkUrlGroups,
  }
  return [heatingResultsComposite]
}

export type VtkUrlGroups = FacilityVtkResult['vtkUrlGroups']

/** Returns whether or not CFD results include temperature data. Useful for determining which
 * inputs to use when calculating thermal comfort metrics.*/
export const isTemperatureSimulation = (vtkUrlGroups: VtkUrlGroups | undefined) => {
  if (!vtkUrlGroups) return false
  if ('temperature' in vtkUrlGroups) {
    return vtkUrlGroups.temperature.seated.length > 0 && vtkUrlGroups.temperature.standing.length > 0
  } else if ('unitHeating' in vtkUrlGroups) {
    return (
      vtkUrlGroups.unitHeating.temperatureBefore.seated.length > 0 &&
      vtkUrlGroups.unitHeating.temperatureBefore.standing.length > 0 &&
      vtkUrlGroups.unitHeating.temperatureAfter.seated.length > 0 &&
      vtkUrlGroups.unitHeating.temperatureAfter.standing.length > 0
    )
  } else if ('temperatureBefore' in vtkUrlGroups && 'temperatureAfter' in vtkUrlGroups) {
    return (
      vtkUrlGroups.temperatureBefore.seated.length > 0 &&
      vtkUrlGroups.temperatureBefore.standing.length > 0 &&
      vtkUrlGroups.temperatureAfter.seated.length > 0 &&
      vtkUrlGroups.temperatureAfter.standing.length > 0
    )
  } else {
    return false
  }
}

export type HeatingCompositeURLs = { radiantHeat: RadiantURLs; unitHeating: UnitHeatURLs }

export type FacilityVtkResult =
  | { type: 'cooling'; vtkUrlGroups: CoolingURLs }
  | { type: 'destrat'; vtkUrlGroups: DestratURLs }
  | { type: 'radiantHeat'; vtkUrlGroups: RadiantURLs }
  | { type: 'unitHeating'; vtkUrlGroups: UnitHeatURLs }
  | { type: 'heatingComposite'; vtkUrlGroups: HeatingCompositeURLs }

export type VtkResult = FacilityVtkResult & { status: string }

export interface FacilityVtkData {
  facility: FacilityDefaults
  vtkResults: VtkResult[]
}
type FacilityVtkResults =
  | { loading: true; error: undefined; data: undefined }
  | { loading: false; error: ApolloError; data: undefined }
  | { loading: false; error: undefined; data: FacilityVtkData }

function urlGroups(internalType: string | null | undefined, urls: URLObject[]): FacilityVtkResult {
  if (internalType == SIMULATION_TYPES.cooling) {
    const data = urls.reduce((accumulator, { url, id, fileProps, fileExtension }) => {
      if (fileProps === undefined) return accumulator

      const isRelevantVtk = fileExtension === 'vtk' && !id.includes('UmBySec') && !id.includes('U_')
      if (isRelevantVtk && fileProps.type === CFD_FILE_PROPS_TYPE.overhead) {
        const vtkType = 'velocity'
        const level = Number(fileProps.level)

        if (HEIGHT_VALUES.standing.includes(level)) {
          accumulator[vtkType][HEIGHTS.standing].push(url)
        }
        if (HEIGHT_VALUES.seated.includes(level)) {
          accumulator[vtkType][HEIGHTS.seated].push(url)
        }
      }
      return accumulator
    }, getCoolingReducerInitialValue())
    return { type: internalType, vtkUrlGroups: data }
  } else if (internalType == SIMULATION_TYPES.destrat) {
    const data = urls.reduce((accumulator, { url, fileProps, fileExtension, id }) => {
      if (fileProps === undefined) return accumulator

      const isDestratFile = fileProps.type === CFD_FILE_PROPS_TYPE.destrat
      const isVelocityFile = fileProps.type === CFD_FILE_PROPS_TYPE.overhead
      const isRelevantVtk = fileExtension === 'vtk' && !id.includes('UmBySec') && !id.includes('U_')
      if (isRelevantVtk && (isDestratFile || isVelocityFile)) {
        if (isDestratFile) accumulator.destrat = url
        else if (isVelocityFile) {
          const level = Number(fileProps.level)

          if (HEIGHT_VALUES.standing.includes(level)) {
            accumulator.velocity[HEIGHTS.standing].push(url)
          }
          if (HEIGHT_VALUES.seated.includes(level)) {
            accumulator.velocity[HEIGHTS.seated].push(url)
          }
        }
      }
      return accumulator
    }, getDestratReducerInitialValue())
    return { type: internalType, vtkUrlGroups: data }
  } else if (internalType == SIMULATION_TYPES.radiantHeat) {
    const data = urls.reduce((accumulator, { url, id, fileExtension }) => {
      const isIntensityFile = fileExtension === 'vtk' && id.includes('intensity_zNormal')
      if (isIntensityFile) accumulator.intensity = url
      return accumulator
    }, getRadiantHeatReducerInitialValue())
    return { type: internalType, vtkUrlGroups: data }
  } else if (internalType == SIMULATION_TYPES.unitHeating) {
    const data = urls.reduce((accumulator, { url, id, fileProps, fileExtension }) => {
      if (fileProps === undefined) return accumulator

      const isZeroTurnoverImage = /z[\d]+inch\.vtk/.test(id)
      const isFullTurnoverImage = /z[\d]+inch-100turnover\.vtk/.test(id)
      const isRelevantVtk = fileExtension === 'vtk' && !id.includes('UmBySec')
      if (isRelevantVtk && (isZeroTurnoverImage || isFullTurnoverImage)) {
        const vtkType = id.includes('UftByMin') ? 'velocity' : 'temperature'
        const vtkGroup = isZeroTurnoverImage ? 'Before' : 'After'
        const key = (vtkType + vtkGroup) as
          | 'temperatureBefore'
          | 'temperatureAfter'
          | 'velocityBefore'
          | 'velocityAfter'

        const level = Number(fileProps.level)

        if (HEIGHT_VALUES.standing.includes(level)) {
          accumulator[key][HEIGHTS.standing].push(url)
        }
        if (HEIGHT_VALUES.seated.includes(level)) {
          accumulator[key][HEIGHTS.seated].push(url)
        }
      }
      return accumulator
    }, getUnitHeatReducerInitialValue())
    return { type: internalType, vtkUrlGroups: data }
  }
  throw new Error(`unknown internalType ${internalType}`)
}

/**
 * Returns grouped vtk files to be used for extrapolating metrics data. Note: although metric simulations
 * return both UmBySec and UftByMin files, we are only interested in the imperial files. Performance metrics
 * are converted for metric facilities *after* the calculations have been run.
 */
export const useFacilityVtkResults = (): FacilityVtkResults => {
  const { versionId } = useParams<{ versionId: string }>()
  const { data, loading, error } = useQuery(GET_VERSION_CFD_RESULTS_WITH_FACILITY, {
    variables: { versionId: versionId! },
    skip: !versionId,
  })

  return useMemo(() => {
    const { facility } = data?.Version?.floor ?? { facility: undefined }
    const facilityDefaults = getFacilityDefaultDetails(facility ?? undefined)
    const vtkResults = data?.Version?.cfd
      .filter(result => !!result)
      .map(({ internalType, URLs, status }) => {
        return {
          ...urlGroups(internalType, parse(array(urlObjectSchema), URLs)),
          status,
        }
      })

    if (!vtkResults || !facilityDefaults) return { loading: false, error, data: undefined } as FacilityVtkResults

    return {
      loading,
      error,
      data: {
        facility: facilityDefaults,
        vtkResults: [...vtkResults, ...getHeatingResultsComposite(vtkResults)],
      },
    } as FacilityVtkResults
  }, [data, loading, error])
}
